import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCancelFormDeliverable } from '../../components/form-list/useCancelFormDeliverable';
import { useDeleteApiCtpsByIdMutation } from '../../app/athenaApiGen';
import { FormList } from '../../components/form-list';
import DeleteConfirmation from '../../components/Modals/DeleteConfirmation';
import { WORKFLOW_PROGRESS_FILTERS } from '../../utils/workflow';
import { createCTPColumns } from './CTPUtils';
import { useCTPList } from './useCTPList';
import {
  openDeleteDialog,
  closeDeleteDialog,
  showSnackbar,
  hideSnackbar,
  setWorkflowFilter,
} from '../../app/ctp/ctpSlice';

/**
 * Mirrors the TR/TAR list layout while preserving existing CTP columns and data.
 */
export default function CTPList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get state from Redux
  const { isDeleteDialogOpen, ctpToDelete, snackbar } = useSelector((state) => state.ctp);
  const workflowFilter = useSelector((state) => state.ctp.workflowFilter);

  const {
    paginationModel,
    setPaginationModel,
    search,
    setSearch,
    rows,
    rowCount,
    loading,
    isError,
    refetch,
    quickFilterCounts,
  } = useCTPList();
  const [deleteCtp, { isLoading: deleting }] = useDeleteApiCtpsByIdMutation();

  const columns = useMemo(() => createCTPColumns(), []);

  const handleDeleteClick = useCallback((row) => {
    dispatch(openDeleteDialog({ id: row.id, ctpNumber: row.ctpNumber }));
  }, [dispatch]);

  const handleDeleteConfirm = useCallback(
    async () => {
      if (!ctpToDelete) return;
      try {
        await deleteCtp({ id: ctpToDelete.id }).unwrap();
        dispatch(closeDeleteDialog());
        dispatch(showSnackbar({ message: 'CTP deleted successfully', severity: 'success' }));
        refetch();
      } catch (error) {
        console.error('Failed to delete CTP', error);
        dispatch(closeDeleteDialog());
        dispatch(showSnackbar({ message: 'Failed to delete CTP. Please try again.', severity: 'error' }));
      }
    },
    [ctpToDelete, deleteCtp, refetch, dispatch],
  );

  const notifyCancel = useCallback(
    (message, severity) => dispatch(showSnackbar({ message, severity })),
    [dispatch],
  );
  const { handleCancelClick } = useCancelFormDeliverable({
    entityLabel: 'CTP',
    notify: notifyCancel,
    refetch,
  });

  const rowActions = useMemo(
    () => [
      {
        label: 'View',
        onClick: (row) => navigate(`/ctp/${row.id}?mode=view`),
      },
      {
        label: 'Edit',
        onClick: (row) => navigate(`/ctp/${row.id}?mode=edit`),
        hidden: (row) => row.workflowState && row.workflowState !== 'Pending',
      },
      {
        label: 'Cancel',
        onClick: (row) => handleCancelClick(row),
        hidden: (row) =>
          !row.workflowState ||
          row.workflowState === 'Pending' ||
          row.workflowState !== 'In Internal Review',
      },
      {
        label: 'Delete',
        onClick: (row) => handleDeleteClick(row),
        hidden: (row) => row.workflowState && row.workflowState !== 'Pending',
      },
    ],
    [handleDeleteClick, handleCancelClick, navigate],
  );

  return (
    <Box sx={{ p: 1, pt: 2, height: '100%', bgcolor: 'transparent' }}>
      <FormList
        title="Consent to Purchase (CTPs)"
        columns={columns}
        rows={rows}
        rowCount={rowCount}
        loading={loading}
        error={isError}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowActions={rowActions}
        onRowClick={(row) => navigate(`/ctp/${row.id}?mode=view`)}
        workflowFilters={WORKFLOW_PROGRESS_FILTERS}
        workflowFilter={workflowFilter}
        onWorkflowFilterChange={(value) => dispatch(setWorkflowFilter(value))}
        quickFilterCounts={quickFilterCounts}
        search={search}
        onSearchChange={setSearch}
        onRefresh={refetch}
        createButtonLabel="New CTP"
        onCreateClick={() => navigate('/ctp')}
      />

      <DeleteConfirmation
        open={isDeleteDialogOpen}
        title="Delete CTP"
        onClose={() => dispatch(closeDeleteDialog())}
        content={{
          mainText: `
            Are you sure you want to delete CTP <strong>${ctpToDelete?.ctpNumber}</strong>?
            <br /><br />
            This action cannot be undone.`,
        }}
        handleConfirmation={handleDeleteConfirm}
        isLoading={deleting}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => dispatch(hideSnackbar())}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => dispatch(hideSnackbar())}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
